import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Star, Briefcase, MapPin, Award, Globe, CheckCircle2 } from "lucide-react";

export default function ContractorProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/contractors/${id}`).then((r) => setData(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  if (!data) return null;

  return (
    <div className="min-h-screen bg-bone">
      <Seo
        title={`${data.name}${data.company ? ` — ${data.company}` : ""}`}
        path={`/pros/${id}`}
        type="profile"
        description={(data.bio || `${data.name} is a vetted pro on LeadTitan with ${data.review_count} reviews and ${data.leads_completed} leads worked.`).slice(0, 180)}
      />
      <Navbar />
      <section className="border-b-2 border-ink">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {data.featured && (
              <div className="overline bg-acid border-2 border-ink inline-block px-3 py-1 mb-4">// Featured Pro</div>
            )}
            <h1 className="font-display font-black text-5xl sm:text-6xl uppercase tracking-tighter">{data.name}</h1>
            {data.company && <p className="font-mono mt-2 uppercase tracking-widest text-sm">{data.company}</p>}
            <div className="flex flex-wrap items-center gap-6 mt-6">
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={`pro-star-${i}`} className={`w-5 h-5 ${i < Math.round(data.rating_avg) ? "fill-acid stroke-ink" : "stroke-ink"}`} />
                ))}
                <span className="font-mono text-sm">{data.rating_avg.toFixed(1)} ({data.review_count})</span>
              </div>
              {data.approved && <span className="overline bg-ink text-acid px-2 py-1 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verified</span>}
              <span className="overline">{data.leads_completed} leads worked</span>
            </div>
            {data.bio && <p className="text-base text-muted-foreground mt-8 max-w-2xl leading-relaxed">{data.bio}</p>}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.years_experience != null && (
                <Info icon={Award} label="Experience" value={`${data.years_experience} yrs`} />
              )}
              {data.service_zips?.length > 0 && (
                <Info icon={MapPin} label="Service area" value={data.service_zips.slice(0, 4).join(", ") + (data.service_zips.length > 4 ? "..." : "")} />
              )}
              {data.website && (
                <Info icon={Globe} label="Website" value={<a href={data.website} className="underline" target="_blank" rel="noreferrer">visit</a>} />
              )}
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="brut-card p-6 bg-ink text-white">
              <div className="overline text-acid">// Trades</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {data.service_categories?.length === 0 && <span className="text-sm text-white/60">No services listed.</span>}
                {data.service_categories?.map((c) => (
                  <span key={c.category_id} className="font-mono text-xs border-2 border-acid bg-acid text-ink px-2 py-1">{c.name}</span>
                ))}
              </div>
              <div className="mt-6 overline text-acid">// Member since</div>
              <div className="font-mono text-xs mt-1">{data.joined ? new Date(data.joined).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
          <div className="overline mb-6">// Reviews</div>
          {data.reviews?.length === 0 ? (
            <p className="font-mono text-sm uppercase tracking-widest">No reviews yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.reviews.map((r) => (
                <div key={r.review_id} className="brut-card p-6 bg-white">
                  <div className="flex items-center gap-1 text-acid">
                    {[...Array(5)].map((_, i) => <Star key={`review-star-${r.review_id}-${i}`} className={`w-4 h-4 ${i < r.rating ? "fill-acid stroke-ink" : "stroke-ink"}`} />)}
                  </div>
                  <p className="mt-3 text-sm">{r.comment}</p>
                  <div className="font-mono text-xs mt-3 uppercase tracking-widest">— {r.customer_name || "Anonymous"} · {new Date(r.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="border-2 border-ink p-4 bg-white">
    <Icon className="w-4 h-4" />
    <div className="overline mt-2">{label}</div>
    <div className="font-mono text-sm mt-1">{value}</div>
  </div>
);
